import React, { useState } from "react";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  useTheme,
  useMediaQuery,
  FormControl,
  Select,
  MenuItem,
  DialogActions,
  Button,
  DialogContent,
  TableHead,
  DialogTitle,
  Dialog,
  IconButton,
} from "@mui/material";
import { Box } from "@mui/system";
import InfoIcon from "@mui/icons-material/Info";
import { useTranslation } from "react-i18next";

const CAccordion = ({
  CHeader = "No Header Available",
  CData = [],
  CRows = [],
  COpen = true,
  onClassChange,
  onCriticalityChange, // Add callback prop for criticality
}) => {
  const [isOpen, setIsOpen] = useState(COpen);
  const [selectedClass, setSelectedClass] = useState("All classes");
  const [selectedCriticality, setSelectedCriticality] = useState("All indicators");
  const theme = useTheme();
  const { t } = useTranslation();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const toggleAccordion = () => setIsOpen(!isOpen);
  const [abcInfoOpen, setAbcInfoOpen] = useState(false);
  const [criticalityInfoOpen, setCriticalityInfoOpen] = useState(false);

  const handleAbcInfoOpen = () => {
    setAbcInfoOpen(true);
  };

  const handleAbcInfoClose = () => {
    setAbcInfoOpen(false);
  };

  const handleCriticalityInfoOpen = () => {
    setCriticalityInfoOpen(true);
  };

  const handleCriticalityInfoClose = () => {
    setCriticalityInfoOpen(false);
  };

  const handleClassChange = (event) => {
    const value = event.target.value;
    setSelectedClass(value);

    if (onClassChange) {
      onClassChange(value);
    }
  };

  const handleCriticalityChange = (event) => {
    const value = event.target.value;
    setSelectedCriticality(value);

    if (onCriticalityChange) {
      onCriticalityChange(value);
    }
  };

  return (
    <div
      style={{
        border: "1px solid #3CB043",
        borderRadius: "12px",
        margin: "10px 0",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        onClick={toggleAccordion}
        style={{
          backgroundColor: "#3CB043",
          color: "#FFFFFF",
          padding: "7px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          cursor: "pointer",
          fontSize: isXs ? ".9em" : "1.2em",
        }}
      >
        <div style={{ flex: 1, textAlign: "center" }}>{CHeader}</div>
        <div>
          {isOpen ? (
            <ExpandLessIcon style={{ color: "#FFFFFF" }} />
          ) : (
            <ExpandMoreIcon style={{ color: "#FFFFFF" }} />
          )}
        </div>
      </div>

      {/* Content - Only visible when open */}
      {isOpen && (
        <div style={{ padding: "10px", backgroundColor: "#FFFFFF" }}>
          <TableContainer>
            <Table size="small">
              <TableBody>
                {CData?.map((actualArr, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {CRows.map((rowData, cellIndex) => (
                      <TableCell
                        component="th"
                        scope="row"
                        key={cellIndex}
                        style={{
                          padding: "0em 0.2em 0em 2em",
                          margin: "0em",
                          border: "none",
                          width:
                            cellIndex === 0
                              ? isXs
                                ? "65%"
                                : "40%"
                              : isXs
                              ? "35%"
                              : "60%",
                        }}
                      >
                        {actualArr?.map((actualRow, innerIndex) => (
                          <TableRow key={innerIndex}>
                            <TableCell
                              component="th"
                              scope="row"
                              style={{
                                textAlign: "left",
                                padding: "0.8em 0em 0.8em 0em",
                                verticalAlign: "middle",
                                fontSize: "1.05em",
                                border: "none",
                              }}
                            >
                              {rowData.type === "T" && actualRow[rowData.value]}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* ABC Class Row */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{
                      padding: "0.8em 0em 0.8em 2em",
                      border: "none",
                      width: isXs ? "65%" : "40%",
                    }}
                  >
                    <div
                      sx={{
                        display: "flex",
                        gap: "5px",
                        justifyContent: "center",
                      }}
                    >
                      <span>ABC {t('Class')}</span>
                      <IconButton
                        size="small"
                        onClick={handleAbcInfoOpen}
                        onMouseEnter={handleAbcInfoOpen}
                        sx={{
                          marginLeft: "5px",
                          color: "#052660",
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>

                  {/* ABC Class Info Dialog */}
                  <Dialog
                    open={abcInfoOpen}
                    onClose={handleAbcInfoClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                      onMouseLeave: handleAbcInfoClose,
                      sx: {
                        pointerEvents: "auto",
                      },
                    }}
                    BackdropProps={{
                      invisible: true,
                    }}
                  >
                    <DialogTitle
                      sx={{
                        backgroundColor: "#3CB043",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {t("ABC Classification Information")}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "1em",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              {t("Classification")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "1em",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              {t("Counting Frequency")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#fff3e0",
                                fontWeight: "600",
                              }}
                            >
                              {t("Class")} A {t("Items")}
                            </TableCell>
                            <TableCell>{t("Once every six months")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#e3f2fd",
                                fontWeight: "600",
                              }}
                            >
                              {t("Class")} B {t("Items")}
                            </TableCell>
                            <TableCell>{t("Once a year")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#f1f8e9",
                                fontWeight: "600",
                              }}
                            >
                              {t("Class")} C {t("Items")}
                            </TableCell>
                            <TableCell>{t("Once a year")}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                      <Button
                        onClick={handleAbcInfoClose}
                        variant="contained"
                        color="primary"
                      >
                        {t("close")}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <TableCell
                    style={{
                      padding: "0.8em 0em 0.8em 0em",
                      border: "none",
                      width: isXs ? "35%" : "60%",
                    }}
                  >
                    <FormControl
                      size="small"
                      style={{
                        minWidth: isXs ? "120px" : "160px",
                      }}
                    >
                      <Select
                        value={selectedClass}
                        onChange={handleClassChange}
                        displayEmpty
                      >
                        <MenuItem value="All classes">{t("All classes")}</MenuItem>
                        <MenuItem value="A">A {t("class")}</MenuItem>
                        <MenuItem value="B">B {t("class")}</MenuItem>
                        <MenuItem value="C">C {t("class")}</MenuItem>
                        <MenuItem value="A&B">A & B {t("class")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>

                {/* Material Criticality Row */}
                <TableRow>
                  <TableCell
                    component="th"
                    scope="row"
                    style={{
                      padding: "0.8em 0em 0.8em 2em",
                      border: "none",
                      width: isXs ? "65%" : "40%",
                    }}
                  >
                    <div
                      sx={{
                        display: "flex",
                        gap: "5px",
                        justifyContent: "center",
                      }}
                    >
                      <span>{t('Material Criticality')}</span>
                      <IconButton
                        size="small"
                        onClick={handleCriticalityInfoOpen}
                        onMouseEnter={handleCriticalityInfoOpen}
                        sx={{
                          marginLeft: "5px",
                          color: "#052660",
                        }}
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </TableCell>

                  {/* Material Criticality Info Dialog */}
                  <Dialog
                    open={criticalityInfoOpen}
                    onClose={handleCriticalityInfoClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                      onMouseLeave: handleCriticalityInfoClose,
                      sx: {
                        pointerEvents: "auto",
                      },
                    }}
                    BackdropProps={{
                      invisible: true,
                    }}
                  >
                    <DialogTitle
                      sx={{
                        backgroundColor: "#3CB043",
                        color: "white",
                        fontWeight: "bold",
                      }}
                    >
                      {t("Material Criticality Information")}
                    </DialogTitle>
                    <DialogContent sx={{ mt: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "1em",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              {t("Indicator")}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                fontSize: "1em",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              {t("Description")}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#ffebee",
                                fontWeight: "600",
                              }}
                            >
                              V
                            </TableCell>
                            <TableCell>{t("Vital")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#fff3e0",
                                fontWeight: "600",
                              }}
                            >
                              E
                            </TableCell>
                            <TableCell>{t("Essential")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#e3f2fd",
                                fontWeight: "600",
                              }}
                            >
                              I
                            </TableCell>
                            <TableCell>{t("Inactive")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#f1f8e9",
                                fontWeight: "600",
                              }}
                            >
                              D
                            </TableCell>
                            <TableCell>{t("Desirable")}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              sx={{
                                backgroundColor: "#fce4ec",
                                fontWeight: "600",
                              }}
                            >
                              O
                            </TableCell>
                            <TableCell>{t("Obsolete")}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                      <Button
                        onClick={handleCriticalityInfoClose}
                        variant="contained"
                        color="primary"
                      >
                        {t("close")}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <TableCell
                    style={{
                      padding: "0.8em 0em 0.8em 0em",
                      border: "none",
                      width: isXs ? "35%" : "60%",
                    }}
                  >
                    <FormControl
                      size="small"
                      style={{
                        minWidth: isXs ? "120px" : "160px",
                      }}
                    >
                      <Select
                        value={selectedCriticality}
                        onChange={handleCriticalityChange}
                        displayEmpty
                      >
                        <MenuItem value="All indicators">{t("All indicators")}</MenuItem>
                        <MenuItem value="V">V - {t("Vital")}</MenuItem>
                        <MenuItem value="E">E - {t("Essential")}</MenuItem>
                        <MenuItem value="I">I  -  {t("Inactive")}</MenuItem>
                        <MenuItem value="D">D - {t("Desirable")}</MenuItem>
                        <MenuItem value="O">O - {t("Obsolete")}</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      )}
    </div>
  );
};

export default CAccordion;